import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { mobile, tablet, useQuery } from 'styles/breakpoints';
import { Question, QuizAnswer } from 'types/quiz';
import { DynamicImage, PrimaryButton, Text } from 'components';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';
import BirthChart from './BirthChart';
import CheckSvg from 'assets/icons/white-check.svg';
import Star from 'assets/icons/limited-edition-star.svg';
import Sticker from 'assets/icons/sticker-bf.svg';
import { Horoscope, Origin } from 'circular-natal-horoscope-js';
import quizSvgs from 'utils/constants/quizSvgs';
import useQuizAnswersBirthDetails from 'utils/hooks/useQuizAnswersBirthDetails';
import CircleType from 'circletype';
import { resolveBookCoverName } from 'utils/hooks/resolveBookCoverName';

interface MultipleCard {
  question: Question;
  onSelect: (answer: QuizAnswer) => void;
}

const BookBuild: FC<MultipleCard> = ({ question, onSelect }) => {
  const { buttonText, label, colorsOptions } = question;
  const [activeColor, setActiveColor] = useState('black');
  const quiz_answers = useSelector(
    (state: AppState) => state.user.quiz_answers,
  );
  const birthChartColor = question?.birthChartColor[activeColor];
  const birthDetails = useQuizAnswersBirthDetails();
  const [showName, setShowName] = useState<boolean>(false);
  const { isTablet } = useQuery();

  const origin = new Origin({
    year: birthDetails?.year,
    month: birthDetails?.month - 1, // 0 = January, 11 = December! That's why -1.
    date: birthDetails?.day,
    hour: birthDetails?.hours,
    minute: birthDetails?.minutes,
    latitude: quiz_answers?.birth_place.lat,
    longitude: quiz_answers?.birth_place.lng,
  });
  const horoscope = new Horoscope({
    origin,
  });

  const celestialBodies = horoscope._celestialBodies.all;

  const moonSignKey = celestialBodies.find(item => item.key === 'moon')?.Sign
    .key;

  const handleColorClick = (colorName: string) => {
    setActiveColor(colorName);
  };

  const renderSvgImage = (svgKey: string) => {
    const Svg = quizSvgs[svgKey];
    return <Svg />;
  };

  const handelButtonClick = () => {
    onSelect({
      color: activeColor,
      contentColor: birthChartColor,
    });
  };

  const handleOnLoad = () => {
    setShowName(true);
  };

  const textRefPlace = useRef<HTMLDivElement>(null);
  const textRefDate = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRefPlace.current) {
      new CircleType(textRefPlace.current).dir(-1).radius(130);
    }
    if (textRefDate.current) {
      new CircleType(textRefDate.current).dir(-1).radius(110);
    }
  }, []);

  const { isNameLongerThan10, isSurameLongerThan10, isSurname, formattedName } =
    resolveBookCoverName(quiz_answers?.name, quiz_answers?.surname);

  return (
    <Container>
      <Title>{label}</Title>

      <ContentContainer>
        <ImageContainer>
          {question?.isLimited && activeColor === 'black' && (
            <StickerContainer>
              <StyledSticker />
            </StickerContainer>
          )}
          {activeColor && (
            <StyledDynamicImage
              src={`booksCovers/${activeColor}.png`}
              alt=""
              // handleOnLoad={handleOnLoad}
            />
          )}
          <BirthChartStyled
            year={birthDetails?.year as number}
            month={birthDetails?.month as number}
            day={birthDetails?.day as number}
            hour={birthDetails?.hours as number}
            minute={birthDetails?.minutes as number}
            latitude={quiz_answers?.birth_place.lat}
            longitude={quiz_answers?.birth_place.lng}
            color={birthChartColor}
            width={isTablet ? 155 : 236}
            height={isTablet ? 155 : 236}
          />
          <Name
            color={activeColor}
            dangerouslySetInnerHTML={{ __html: formattedName }}
            isSurname={isSurname}
            isNameLongerThan10={isNameLongerThan10}
            isSurameLongerThan10={isSurameLongerThan10}
          />
          <Date color={activeColor} ref={textRefDate}>
            {birthDetails && birthDetails.fullDate}
          </Date>

          <City color={activeColor} ref={textRefPlace}>
            {`${quiz_answers?.birth_place.main_text}, ${quiz_answers?.birth_place.secondary_text}`}
          </City>
          <SunSign color={birthChartColor}>
            <SunText color={activeColor}>{question?.sunText}</SunText>
            <SvgSign>
              {renderSvgImage(`${activeColor}${quiz_answers?.zodiacSign}`)}
            </SvgSign>
          </SunSign>
          <MoonSign color={birthChartColor}>
            <SunText color={activeColor}>{question?.moonText}</SunText>
            <SvgSign>{renderSvgImage(`${activeColor}${moonSignKey}`)}</SvgSign>
          </MoonSign>
        </ImageContainer>

        {colorsOptions && (
          <MainColorsContainer>
            <Label>{question?.colorLabel}</Label>
            <ColorsContainer>
              {Object.entries(colorsOptions).map(([colorName, colorValue]) => (
                <ColorContainer
                  key={colorName}
                  bgColor={colorValue}
                  onClick={() => handleColorClick(colorName)}
                  isActive={colorName === activeColor}
                >
                  {question?.isLimited && colorName === 'black' && (
                    <LimitedEditionStar />
                  )}

                  {colorName === activeColor && <CheckIcon />}
                </ColorContainer>
              ))}
            </ColorsContainer>
          </MainColorsContainer>
        )}
        <FixedButtonBox>
          <StyledButton onClick={handelButtonClick}>{buttonText}</StyledButton>
        </FixedButtonBox>
      </ContentContainer>
    </Container>
  );
};

export default BookBuild;

const StickerContainer = styled.section`
  position: absolute;
  top: -1.5rem;
  right: -3rem;
  z-index: 5;
  @media ${tablet} {
    top: -1.2rem;
    right: -2.9rem;
    svg {
      width: 5.75rem;
      height: 5.75rem;
    }
    width: 5.75rem;
    height: 5.75rem;
  }
`;

const StyledSticker = styled(Sticker)``;

const LimitedEditionStar = styled(Star)`
  position: absolute;
  top: -0.44rem;
  right: -0.44rem;
`;

const Banner = styled.section`
  display: flex;
  padding: 0.625rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.25rem;
  background: #ffd9e7;
  margin-top: 2.25rem;
  max-width: 20.6rem;
  width: 100%;
  @media ${tablet} {
    margin-top: 1.25rem;
  }
`;

const BannerText = styled.p`
  color: #000;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  span {
    color: #ef71a0;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 3.5rem 0px 7rem;
  gap: 1.75rem;
  @media ${tablet} {
    padding: 1.5rem 1rem;
    gap: 1rem;
  }
`;
const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  @media ${tablet} {
    padding-bottom: 80px;
  }
`;

const Title = styled.p`
  color: #000;
  text-align: center;
  font-size: 1.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: 2.25rem;
  @media ${tablet} {
    font-size: 1.5rem;
    line-height: 1.8125rem;
    font-weight: 700;
  }
`;

const FixedButtonBox = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem 1rem;
  width: 100%;
  @media ${tablet} {
    padding: 1rem;
  }
`;

const BirthChartStyled = styled(BirthChart)`
  position: absolute !important;
  top: 0;
  div#paper {
    position: absolute !important;
    top: 0;
  }
`;

const SunSign = styled.div<{ color: string }>`
  display: flex;
  width: 46.773px;
  height: 46.773px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 23.386px;
  border: 0.532px solid ${({ color }) => color};
  position: absolute;
  bottom: 19px;
  left: 22px;
  gap: 2px;

  @media ${tablet} {
    width: 30px;
    height: 30px;
    bottom: 9px;
    left: 12px;
    gap: 1px;
  }
`;

const MoonSign = styled(SunSign)`
  bottom: 19px;
  right: 20px;
  left: unset;
  @media ${tablet} {
    bottom: 9px;
    right: 12px;
  }
`;
const SunText = styled.p<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  text-align: center;
  font-size: 6.236px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: 'Montserrat', sans-serif;
  max-width: 20px;
  position: relative;
  @media ${tablet} {
    font-size: 4px;
    max-width: 19px;
  }
`;

const SvgSign = styled.div`
  width: 15.591px;
  height: 15.591px;
  position: relative;
  svg {
    position: absolute;
  }
  @media ${tablet} {
    width: 10px;
    height: 10px;
  }
`;

const Name = styled.p<{
  color: string;
  isSurname: boolean;
  isNameLongerThan10: boolean;
  isSurameLongerThan10: boolean;
}>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  text-align: center;
  font-family: 'Arno Pro';
  font-size: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '19px' : '21.827px'};
  font-style: normal;
  font-weight: 400;
  line-height: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '20px' : '22px'};
  letter-spacing: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '2px' : '3px'};
  text-transform: uppercase;
  position: absolute;
  ${({ isSurname }) => !isSurname && 'top: 5.3rem;'}
  ${({ isSurname, isNameLongerThan10, isSurameLongerThan10 }) =>
    isSurname && (isNameLongerThan10 || isSurameLongerThan10) && 'top: 4.8rem;'}
  ${({ isSurname, isNameLongerThan10, isSurameLongerThan10 }) =>
    isSurname &&
    !(isNameLongerThan10 || isSurameLongerThan10) &&
    'top: 4.6rem;'}

  right: 50%;
  transform: translateX(50%);
  text-wrap: nowrap;
  @media ${tablet} {
    font-size: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
      isNameLongerThan10 || isSurameLongerThan10 ? '11px' : '13px'};
    line-height: 13px;
    top: ${({ isSurname }) => (isSurname ? '2.9rem' : '3.4rem')};
    letter-spacing: 2px;
  }
`;

const Date = styled.p<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  font-family: 'Montserrat';
  font-size: 9.288px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.3rem; /* Reduced letter-spacing */
  position: absolute;
  bottom: 4.1rem;
  right: 50%;
  transform: translateX(50%);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 200px;
  text-transform: capitalize;
  @media ${tablet} {
    font-size: 6px;
    width: 100px;
    bottom: 2.85rem;
    letter-spacing: 0.2rem;
  }
`;

const City = styled(Date)<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  bottom: 3rem;
  @media ${tablet} {
    bottom: 2rem;
  }
`;

const StyledButton = styled(PrimaryButton)<{ $disabled?: boolean }>`
  width: 100%;
  max-width: 28.75rem;
  margin: 0 auto;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const Label = styled(Text)`
  color: #000;
  text-align: center;
  font-size: 0.875rem;
  @media ${tablet} {
  }
`;

const ImageContainer = styled.div`
  position: relative;
  div#paper {
    position: absolute !important;
    top: 117px;
    right: 50%;
    transform: translateX(50%);
  }
  @media ${tablet} {
    div#paper {
      top: 70px;
    }
  }
`;

const StyledDynamicImage = styled(DynamicImage)`
  width: 343px;
  background-size: contain;
  @media ${tablet} {
    width: 220px;
  }
`;

const MainColorsContainer = styled.div`
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  @media ${tablet} {
    padding-top: 0.75rem;
  }
`;

const ColorsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  height: 3.25rem;
  width: 19.5rem;
  justify-content: space-between;
`;

const ColorContainer = styled.div<{ bgColor: string; isActive: boolean }>`
  display: flex;
  width: ${({ isActive }) => (isActive ? '3.25rem' : '2.5rem')};
  height: ${({ isActive }) => (isActive ? '3.25rem' : '2.5rem')};
  position: relative;
  padding: 1.05625rem 0.89375rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 6.25rem;
  border: 3px solid #fff;
  box-shadow: 0px 0.65px 0.65px 0px rgba(0, 0, 0, 0.25);
  background: ${({ bgColor }) => bgColor};
`;

const CheckIcon = styled(CheckSvg)`
  width: 1.125rem;
  height: 0.84375rem;
`;
